import React from "react";
import { Trans } from "@coworker/locales";
import { ItemWithPieces } from "@coworker/apprestructured/src/shared/types/item";
import { ItemNoAndTypeWithOrderedDetailsAcknowledge } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { Loading } from "../../../loading";
import FixaCheckbox from "@coworker/apprestructured/src/shared/wrappers/FixaCheckbox/FixaCheckbox";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import styles from "./ArticlesPrintPTagAndQuantityAcknowledgeContent.module.css";
import { ItemCardWithPieces } from "@coworker/apprestructured/src/shared/components/item-cards/ItemCardWithPieces/ItemCardWithPieces";

interface ArticlesPrintPTagAndQuantityAcknowledgeContentProps {
  items: ItemWithPieces[];
  itemNosAndTypeWithOrderedDetails:
    | ItemNoAndTypeWithOrderedDetailsAcknowledge[]
    | null
    | Loading;
  pieces?: boolean;
  pTags?: boolean;
  onAcknowledgeChange: (
    itemNo: string,
    field: "pieces_acknowledge" | "ptags_acknowledge",
    approved: boolean
  ) => void;
}

type CheckboxRowProps = {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  value: string;
  translationKey: string;
  count: number;
};

const CheckboxRow = ({
  id,
  checked,
  onChange,
  value,
  translationKey,
  count,
}: CheckboxRowProps) => (
  <div className={styles["checkbox-text-row"]}>
    <FixaCheckbox
      id={id}
      value={value}
      checked={checked}
      onChange={() => onChange(!checked)}
    />
    <FixaText
      tagName="p"
      textStyle="Body.M"
      className={checked ? styles["strikethrough"] : undefined}
    >
      <Trans values={{ count }}>{translationKey}</Trans>
    </FixaText>
  </div>
);

export const ArticlesPrintPTagAndQuantityAcknowledgeContent = ({
  items,
  itemNosAndTypeWithOrderedDetails,
  pieces = true,
  pTags = true,
  onAcknowledgeChange,
}: ArticlesPrintPTagAndQuantityAcknowledgeContentProps) => {
  const orderedDetailsByItemNo = new Map(
    Array.isArray(itemNosAndTypeWithOrderedDetails)
      ? itemNosAndTypeWithOrderedDetails.map(
          ({
            no,
            pieces_ordered,
            ptags_ordered,
            ptags_acknowledge,
            pieces_acknowledge,
          }) => [
            no,
            {
              pieces_ordered,
              ptags_ordered,
              ptags_acknowledge,
              pieces_acknowledge,
            },
          ]
        )
      : []
  );

  return (
    <>
      {items.map((item) => (
        <React.Fragment key={item.no}>
          <ItemCardWithPieces item={item} />
          <div className={styles["checkbox-rows"]}>
            {pieces && (
              <CheckboxRow
                id={`item-get-articles-checkbox-${item.no}`}
                value={item.no}
                checked={
                  !!orderedDetailsByItemNo.get(item.no)?.pieces_acknowledge
                }
                onChange={(checked) =>
                  onAcknowledgeChange(item.no, "pieces_acknowledge", checked)
                }
                translationKey="getPiecesString"
                count={orderedDetailsByItemNo.get(item.no)?.pieces_ordered ?? 0}
              />
            )}
            {pTags && (
              <CheckboxRow
                id={`item-print-ptags-checkbox-${item.no}`}
                value={item.no}
                checked={
                  !!orderedDetailsByItemNo.get(item.no)?.ptags_acknowledge
                }
                onChange={(checked) =>
                  onAcknowledgeChange(item.no, "ptags_acknowledge", checked)
                }
                translationKey="pTagsString"
                count={orderedDetailsByItemNo.get(item.no)?.ptags_ordered ?? 0}
              />
            )}
          </div>
        </React.Fragment>
      ))}
    </>
  );
};
